import { useMemo } from "react";

import { Scenario, useScenario as useScenarioApi } from "../api/getScenario";
import { useScenarioV2 as useScenarioV2Api } from "../api/v2/getScenario";
import { useScenarioResultsV2 } from "../api/v2/getScenarioResults";

type UseScenarioV2Options = {
  uuid: string;
  isScenariosApiEnabled: boolean;
  isGeoEnabled?: boolean;
};

export const useScenarioV2 = ({
  uuid,
  isScenariosApiEnabled,
  isGeoEnabled = false,
}: UseScenarioV2Options) => {
  const { data: scenarioV1, isLoading: isScenarioLoadingV1 } = useScenarioApi({
    scenarioUUID: uuid,
    breakdownResultsGeography: isGeoEnabled,
    config: {
      refetchInterval: 0,
      refetchOnWindowFocus: false,
      enabled: !isScenariosApiEnabled,
    },
  });

  const { data: scenarioV2, isLoading: isScenarioLoadingV2 } = useScenarioV2Api({
    uuid,
    config: {
      enabled: isScenariosApiEnabled,
    },
  });

  const { data: scenarioResultsV2, isLoading: isScenarioResultsLoadingV2 } = useScenarioResultsV2({
    uuid,
    config: {
      enabled:
        isScenariosApiEnabled &&
        !!scenarioV2 &&
        !!scenarioV2.scenarioStatus &&
        scenarioV2?.scenarioStatus !== "error" &&
        scenarioV2?.scenarioStatus !== "processing" &&
        scenarioV2?.scenarioStatus !== "unknown" &&
        scenarioV2?.scenarioStatus !== "draft",
    },
  });

  const combinedScenarioV2 = useMemo((): Scenario | undefined => {
    if (!scenarioV2) return undefined;
    if (!scenarioResultsV2) return scenarioV2;
    return {
      ...scenarioV2,
      scenarioReport: scenarioResultsV2,
    };
  }, [scenarioV2, scenarioResultsV2]);

  const scenario = isScenariosApiEnabled ? combinedScenarioV2 : scenarioV1;
  const isScenarioLoading = isScenariosApiEnabled
    ? isScenarioLoadingV2 || isScenarioResultsLoadingV2
    : isScenarioLoadingV1;

  return {
    scenario,
    isScenarioLoading,
  };
};
